import { useMemo } from 'react';
import { PRIMARY_INDUSTRIES } from '@/constants/industries';
import { find } from 'lodash';
import { getReadableCountry } from '@/utilities/markets';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { ROUTE_DISCOVER_INVESTING_IDEAS } from '@/constants/routes';
import type { PageType } from '@/pages/DiscoverPageV2/constants';

interface BreadCrumbLinks {
  name: string;
  dispatch?: ReducerAction;
}

interface Company {
  shortName: string;
  tickerSymbol: string;
}

interface TitlePayload {
  demonymCountry: string;
  index?: string;
  industry?: string;
  question?: string;
  companies?: Company[];
  type: PageType;
}
/**
 * FAQ Question
 */
export const getQuestion = ({
  demonymCountry,
  index,
  industry,
  type,
}: TitlePayload) => {
  let question = `What are the best ${demonymCountry}`;
  if (typeof index !== 'undefined') {
    question += ` (${index})`;
  }
  if (typeof industry !== 'undefined') {
    question += ` ${industry}`;
  }
  question += ` ${type} Stocks?`;
  return question;
};
/**
 * FAQ Answer
 */
export const getAnswer = ({
  demonymCountry,
  index,
  industry,
  companies,
  type,
}: TitlePayload) => {
  if (typeof companies === 'undefined' || companies.length === 0) {
    return `There are 0 companies in the ${demonymCountry} market that meet our criteria.`;
  }
  let answer = `The best ${demonymCountry}`;
  if (typeof index !== 'undefined') {
    answer += ` (${index})`;
  }
  if (typeof industry !== 'undefined') {
    answer += ` ${industry}`;
  }
  answer += ` ${type} ${
    companies.length === 1 ? 'company' : 'companies'
  } according to our Simply Wall St analysis`;
  answer += companies.length === 1 ? ' is ' : ' are ';
  answer += companies.slice(0, 3).reduce((a, b, i, arr) => {
    const company = `${b.shortName} (${b.tickerSymbol})`;
    if (i === 0) {
      return a + company;
    }
    if (i > 0 && i < arr.length - 1) {
      return a + `, ${company}`;
    }
    return a + ` and ${company}`;
  }, '');
  return answer + '.';
};

/**
 * Header Titles
 */
export const getTitle = ({
  demonymCountry,
  index,
  industry,
  type,
}: TitlePayload) => {
  let title = 'Top ';
  title += demonymCountry;
  if (typeof index !== 'undefined') {
    title += ` (${index})`;
  }
  if (typeof industry !== 'undefined') {
    title += ` ${industry}`;
  }
  title += ` ${type} Stocks`;
  return title;
};
/**
 * Meta Titles
 */
export const getMetaTitle = ({
  demonymCountry,
  index,
  industry,
  type,
}: TitlePayload) => {
  let title = 'Best ';
  title += demonymCountry;
  if (typeof index !== 'undefined') {
    title += ` (${index})`;
  }
  if (typeof industry !== 'undefined') {
    title += ` ${industry}`;
  }
  title += ` ${type} Stocks`;
  return title;
};
/**
 * Header Descriptions
 */
export const getDescription = ({
  demonymCountry,
  industry,
  type,
}: TitlePayload) => {
  let description = `According to our Simply Wall St analysis these are the best ${demonymCountry}`;
  if (typeof industry !== 'undefined') {
    description += ` ${industry}`;
  }
  description += ` ${type.toLowerCase()} companies. `;
  if (type === 'Dividend') {
    description +=
      'We look for companies with high quality dividends and healthy balance sheets to find the top Dividend Stocks.';
  } else if (type === 'Gold') {
    description +=
      'We look for companies that generate strong returns and have healthy balance sheets or cash positions compared to their peers.';
  } else if (type === 'Growth') {
    description +=
      'We look for companies with high forecasted growth and healthy balance sheets that can deliver sustained growth over the long term.';
  }
  return description;
};
/**
 * Meta Descriptions
 */
export const getMetaDescription = ({
  demonymCountry,
  index,
  industry,
  type,
}: TitlePayload) => {
  let description = `Discover the best ${demonymCountry}`;
  if (typeof industry !== 'undefined') {
    description += ` ${industry}`;
  }
  description += ` ${type} Stocks`;
  description += typeof index !== 'undefined' ? ` on the ${index}.` : '.';
  if (type === 'Dividend') {
    description +=
      ' These long term dividend stocks have high yields and healthy balance sheets.';
  }
  return description;
};

export default function useMetaData(
  pageType: PageType,
  market: PrimaryCountryISOElseGlobal,
  industry,
  companies: Company[]
) {
  const demonymCountry = getReadableCountry(market);
  const marketMainIndex = getReadableCountry(market, 'mainIndex');
  const selectedIndustryItem = find(PRIMARY_INDUSTRIES, {
    slug: industry,
  });
  const readableIndustryLabel = selectedIndustryItem
    ? selectedIndustryItem.label
    : 'Any';
  let safeIndex: string | undefined;
  let safeIndustry: string | undefined;
  if (market !== 'global' && market !== 'us') {
    safeIndex = marketMainIndex;
  }
  if (readableIndustryLabel !== 'Any') {
    safeIndustry = readableIndustryLabel;
  }
  /**
   * FAQ Question
   */
  const question = useMemo(() => {
    return getQuestion({
      demonymCountry,
      index: safeIndex,
      industry: safeIndustry,
      type: pageType,
    });
  }, [pageType, safeIndex, safeIndustry, demonymCountry]);
  /**
   * FAQ Answer
   */
  const answer = useMemo(() => {
    return getAnswer({
      demonymCountry,
      index: safeIndex,
      industry: safeIndustry,
      companies,
      type: pageType,
    });
  }, [pageType, safeIndex, safeIndustry, demonymCountry, companies]);
  /**
   * Title
   */
  const title = useMemo(() => {
    return getTitle({
      demonymCountry,
      index: safeIndex,
      industry: safeIndustry,
      type: pageType,
    });
  }, [pageType, safeIndex, safeIndustry, demonymCountry]);
  /**
   * Description
   */
  const description = useMemo(() => {
    return getDescription({
      demonymCountry,
      index: safeIndex,
      industry: safeIndustry,
      question,
      type: pageType,
    });
  }, [pageType, safeIndex, safeIndustry, demonymCountry, question]);
  /**
   * Meta Title
   */
  const metaTitle = useMemo(() => {
    return getMetaTitle({
      demonymCountry,
      index: safeIndex,
      industry: safeIndustry,
      type: pageType,
    });
  }, [pageType, safeIndex, safeIndustry, demonymCountry]);
  /**
   * Meta Description
   */
  const metaDescription = useMemo(() => {
    return getMetaDescription({
      demonymCountry,
      index: safeIndex,
      industry: safeIndustry,
      type: pageType,
    });
  }, [pageType, safeIndex, safeIndustry, demonymCountry]);
  /**
   * Bread Crumbs
   */
  const breadCrumbs = useMemo(
    (): BreadCrumbLinks[] => [
      {
        name: 'Investing Ideas',
        dispatch: { type: ROUTE_DISCOVER_INVESTING_IDEAS },
      },
      {
        name: `Top ${pageType} Stocks`,
      },
    ],
    [pageType]
  );

  return {
    title,
    description,
    metaTitle,
    metaDescription,
    question,
    answer,
    demonymCountry,
    breadCrumbs,
  };
}
